import { useQuery } from '@tanstack/react-query';
import { Building2, Database, FileText, Loader2, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { partnerApi } from '../../lib/api';
import { useTenants } from '../tenant-selector/useTenants';
import { Button } from '../ui/button';
import { Select, type SelectOption } from '../ui/select';

const PAGE_SIZE = 50;

export const ResourceCountsTab: React.FC = () => {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [selectedTenantExternalId, setSelectedTenantExternalId] = useState<string | null>(null);

  const { data: tenants = [] } = useTenants();

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['resourceCountsForPartner', cursor, selectedTenantExternalId],
    queryFn: () =>
      partnerApi.getResourceCountsForPartner({
        cursor,
        limit: PAGE_SIZE,
        tenantExternalId: selectedTenantExternalId || undefined,
      }),
  });

  // Reset pagination when tenant filter changes
  useEffect(() => {
    setCursor(undefined);
    setCursorHistory([]);
  }, [selectedTenantExternalId]);

  const handleRefresh = () => {
    setCursor(undefined);
    setCursorHistory([]);
    refetch();
  };

  const tenantMap = new Map<string, string>();

  tenants.forEach(tenant => {
    if (tenant.externalId) {
      tenantMap.set(tenant.externalId, tenant.name);
    }
  });

  // Add tenant external IDs from the resource counts data
  data?.results?.forEach(count => {
    if (count.tenantExternalId && !tenantMap.has(count.tenantExternalId)) {
      tenantMap.set(count.tenantExternalId, count.tenantExternalId);
    }
  });

  // Build options array
  const tenantOptions: SelectOption[] = [
    { value: '', label: 'All Tenants' },
    ...Array.from(tenantMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([externalId, name]) => ({
        value: externalId,
        label: name === externalId ? externalId : `${name} (${externalId})`,
      })),
  ];

  // Add "(No External ID)" option if there are any tenants without external IDs in the data
  if (data?.results.some(r => r.tenantExternalId === null)) {
    const hasNoExternalIdOption = tenantOptions.some(opt => opt.value === '(No External ID)');
    if (!hasNoExternalIdOption) {
      tenantOptions.push({
        value: '(No External ID)',
        label: '(No External ID)',
      });
    }
  }

  const handleNextPage = () => {
    if (data?.pagination.cursor) {
      setCursorHistory(prev => [...prev, cursor || '']);
      setCursor(data.pagination.cursor);
    }
  };

  const handlePreviousPage = () => {
    if (cursorHistory.length > 0) {
      const previousCursor = cursorHistory[cursorHistory.length - 1];
      setCursorHistory(prev => prev.slice(0, -1));
      setCursor(previousCursor || undefined);
    } else {
      setCursor(undefined);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading resource counts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-red-800">Error loading resource counts</p>
            <p className="text-sm text-red-600 mt-1">Please try again later.</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefetching}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!data || !data.results || data.results.length === 0) {
    return (
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
        <div className="flex items-center justify-center py-12">
          <Database className="w-12 h-12 text-gray-400" />
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-900">No resources found</h3>
            <p className="text-sm text-gray-500">
              {selectedTenantExternalId
                ? `No resources found for the selected tenant.`
                : 'No resources have been synced yet for any tenants.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Group counts by tenant external ID
  const countsByTenant = data.results.reduce(
    (acc, count) => {
      const tenantId = count.tenantExternalId || '(No External ID)';
      if (!acc[tenantId]) {
        acc[tenantId] = [];
      }
      acc[tenantId].push(count);
      return acc;
    },
    {} as Record<
      string,
      Array<{
        tenantExternalId: string | null;
        schemaDefinitionId: string;
        schemaDefinitionName: string;
        count: number;
      }>
    >,
  );

  // Sort tenants by total count (descending)
  const tenantEntries = Object.entries(countsByTenant).sort((a, b) => {
    const totalA = a[1].reduce((sum, c) => sum + c.count, 0);
    const totalB = b[1].reduce((sum, c) => sum + c.count, 0);
    return totalB - totalA;
  });

  // Calculate totals
  // When no tenant filter is selected, use aggregates (all data across all tenants)
  // When a tenant is selected, calculate from current results (which are all results for that tenant)
  const totalResources = selectedTenantExternalId
    ? data.results.reduce((sum, c) => sum + c.count, 0)
    : data.aggregates.totalResources;
  const totalTenants = selectedTenantExternalId
    ? 1 // When filtered, there's only one tenant
    : data.aggregates.totalTenants;
  const totalSchemas = selectedTenantExternalId
    ? new Set(data.results.map(c => c.schemaDefinitionId)).size
    : data.aggregates.totalSchemas;

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Resource Counts</h2>
          <p className="mt-1 text-sm text-gray-500">
            View resource counts across all tenants grouped by schema definition
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefetching}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tenant Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="tenant-filter" className="text-sm font-medium text-gray-700">
            Filter by Tenant:
          </label>
          <Select
            options={tenantOptions}
            value={selectedTenantExternalId || ''}
            onChange={value => setSelectedTenantExternalId(value || null)}
            placeholder="All Tenants"
            searchable={true}
            className="min-w-[250px]"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Database className="w-8 h-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Resources</p>
              <p className="text-2xl font-semibold text-gray-900">{totalResources.toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Tenants</p>
              <p className="text-2xl font-semibold text-gray-900">{totalTenants}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="w-8 h-8 text-purple-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Schema Types</p>
              <p className="text-2xl font-semibold text-gray-900">{totalSchemas}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Counts by Tenant */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Resource Counts by Tenant & Schema</h3>
          <p className="mt-1 text-sm text-gray-500">
            View resource counts grouped by tenant external ID and schema definition
          </p>
        </div>
        <div className="divide-y divide-gray-200">
          {tenantEntries.map(([tenantId, counts]) => {
            const tenantTotal = counts.reduce((sum, c) => sum + c.count, 0);
            return (
              <div key={tenantId} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-base font-medium text-gray-900">Tenant: {tenantId}</h4>
                    <p className="text-sm text-gray-500">
                      Total: {tenantTotal.toLocaleString()} resources
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Schema Definition
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {counts
                        .sort((a, b) => b.count - a.count)
                        .map(count => (
                          <tr key={count.schemaDefinitionId}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {count.schemaDefinitionName}
                                </div>
                                <div className="text-xs text-gray-500">{count.schemaDefinitionId}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                              {count.count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {(() => {
                const currentPage = cursorHistory.length + 1;
                const total = data.pagination.total;

                // Count the number of unique tenant groups in current results
                const uniqueTenants = new Set(
                  data.results.map(r => r.tenantExternalId || '(No External ID)'),
                ).size;

                const startItem = (currentPage - 1) * PAGE_SIZE + 1;
                const endItem = startItem + uniqueTenants - 1;

                if (isLoading) return 'Loading...';
                if (data.results.length === 0) return 'No results found';

                return `Viewing ${startItem}-${endItem} of ${total} tenant groups`;
              })()}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={cursorHistory.length === 0 || isLoading}>
                Previous
              </Button>
              <span className="text-sm text-gray-700">Page {cursorHistory.length + 1}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!data.pagination.hasNextPage || isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Next'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
